import { getCookie } from 'cookies-next';

import { appLogger } from '@/utils/app-logger/appLogger';
import { SESSION_REFRESH_IN_SEC_COOKIE } from '@/utils/constants/common';
import {
    getHoursInMS,
    getMinsInMS,
    getSecondsInMS,
    parsePositiveSeconds,
} from '@/utils/dates/dates';
import { describeSessionJwt } from '@/utils/session/sessionJwt';

export const DEFAULT_SESSION_REFRESH_INTERVAL_MS = getHoursInMS(4);

/** Refresh the JWT this long before the cookie TTL expires. */
export const PLATFORM_RESTART_SAFETY_MARGIN_MS = getMinsInMS(5);

/** Minimum delay passed to setTimeout — avoids immediate restarts and client-start races. */
export const MIN_PLATFORM_RESTART_DELAY_MS = PLATFORM_RESTART_SAFETY_MARGIN_MS;

/** Browser setTimeout maximum delay (2^31 - 1 ms). */
export const MAX_SETTIMEOUT_DELAY_MS = 2 ** 31 - 1;

const DEFAULT_PLATFORM_RESTART_DELAY_MS =
    DEFAULT_SESSION_REFRESH_INTERVAL_MS - PLATFORM_RESTART_SAFETY_MARGIN_MS;

function clampPlatformRestartDelayMs(delayMs: number): number {
    return Math.min(Math.max(delayMs, MIN_PLATFORM_RESTART_DELAY_MS), MAX_SETTIMEOUT_DELAY_MS);
}

// Cross-MFE signal: only the live `window` is shared between separately-bundled MFEs, so awaiters
// coordinate through this event rather than a shared module.
export const JWT_TOKEN_SET_EVENT = 'ptv:jwt-token-set';

// Visibility into session-token churn: logs every real change to `window.jwtToken` and
// `window.refreshJwt` via appLogger — reaches observability in every env and is mirrored to the
// console only in dev (local + Platypus), where _app registers the console backend.
function logWindowJwtChange(key: 'jwtToken' | 'refreshJwt', previous: string, next: string): void {
    if (previous === next) return;
    appLogger.info(
        `PlatformSession:jwt window.${key}: ${describeSessionJwt(previous)} -> ${describeSessionJwt(next)}`,
    );
}

export function getWindowJwtToken(): string {
    if (globalThis.window === undefined) return '';

    const windowWithJwt = globalThis.window as Window & { jwtToken?: string };
    const token = windowWithJwt.jwtToken?.trim() ?? '';
    return token;
}

export function setWindowJwtToken(jwtToken: string): void {
    if (globalThis.window === undefined) return;

    const windowWithJwt = globalThis.window as Window & { jwtToken?: string };
    const previousToken = windowWithJwt.jwtToken ?? '';
    const token = jwtToken.trim();
    windowWithJwt.jwtToken = token;
    logWindowJwtChange('jwtToken', previousToken, token);

    // Signal awaiters only on a real write — an empty token means the session isn't ready.
    if (token) {
        globalThis.window.dispatchEvent(new Event(JWT_TOKEN_SET_EVENT));
    }
}

/**
 * Discards a token that proved unusable (401 or expired), but only while it is still the published
 * one — a concurrent restart may have replaced it, and clearing that would strand a healthy session.
 *
 * @returns whether the token slot was cleared.
 */
export function clearWindowJwtTokenIfCurrent(rejectedJwt: string): boolean {
    if (globalThis.window === undefined) return false;

    const rejected = rejectedJwt.trim();
    if (!rejected || getWindowJwtToken() !== rejected) return false;

    setWindowJwtToken('');
    return true;
}

type WindowWithRefreshJwt = Window & { refreshJwt?: string };

/**
 * Restart-only JWT. An incomplete cold-start token must stay off `window.jwtToken` (the player reads
 * that), but platformRestart fails without one — so it is parked here, on `window` for cross-MFE reach.
 */
export function getWindowRefreshJwt(): string {
    if (globalThis.window === undefined) return '';

    const windowWithRefreshJwt = globalThis.window as WindowWithRefreshJwt;
    return windowWithRefreshJwt.refreshJwt?.trim() ?? '';
}

export function setWindowRefreshJwt(refreshJwt: string): void {
    if (globalThis.window === undefined) return;

    const windowWithRefreshJwt = globalThis.window as WindowWithRefreshJwt;
    const previousRefreshJwt = windowWithRefreshJwt.refreshJwt ?? '';
    const nextRefreshJwt = refreshJwt.trim();
    windowWithRefreshJwt.refreshJwt = nextRefreshJwt;
    logWindowJwtChange('refreshJwt', previousRefreshJwt, nextRefreshJwt);
}

export function clearWindowRefreshJwt(): void {
    if (globalThis.window === undefined) return;

    const windowWithRefreshJwt = globalThis.window as WindowWithRefreshJwt;
    const previousRefreshJwt = windowWithRefreshJwt.refreshJwt ?? '';
    delete windowWithRefreshJwt.refreshJwt;
    logWindowJwtChange('refreshJwt', previousRefreshJwt, '');
}

/**
 * Discards the cold-start seed once the platform has refused it as restart input, so later recoveries
 * go straight to Start instead of restarting from a token that can only be refused again. Compare-
 * and-swap like `clearWindowJwtTokenIfCurrent`: a seed set since is not the one that was refused.
 *
 * @returns whether the seed slot was cleared.
 */
export function clearWindowRefreshJwtIfCurrent(rejectedJwt: string): boolean {
    if (globalThis.window === undefined) return false;

    const rejected = rejectedJwt.trim();
    if (!rejected || getWindowRefreshJwt() !== rejected) return false;

    clearWindowRefreshJwt();
    return true;
}

/** JWT a restart should refresh from: the published token if there is one, else the cold-start seed. */
export function resolveRestartSessionJwt(): string {
    return getWindowJwtToken() || getWindowRefreshJwt();
}

/** Calls `listener` whenever a non-empty `window.jwtToken` is published; returns the unsubscribe. */
export function subscribeToWindowJwtToken(listener: () => void): () => void {
    if (globalThis.window === undefined) return () => {};

    globalThis.window.addEventListener(JWT_TOKEN_SET_EVENT, listener);
    return () => globalThis.window.removeEventListener(JWT_TOKEN_SET_EVENT, listener);
}

/**
 * Resolves with `window.jwtToken` once it is non-empty, or with the current value on timeout
 * (never rejects). Lets a consumer defer work until start/restart publishes the token.
 */
export function waitForWindowJwtToken(timeoutMs: number): Promise<string> {
    if (globalThis.window === undefined) return Promise.resolve('');

    const existing = getWindowJwtToken();
    if (existing) return Promise.resolve(existing);

    return new Promise((resolve) => {
        let settled = false;
        let cleanup = () => {};

        const settle = (token: string) => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(token);
        };

        const onSet = () => {
            const token = getWindowJwtToken();
            if (token) settle(token);
        };

        const timer = setTimeout(() => settle(getWindowJwtToken()), timeoutMs);
        const unsubscribe = subscribeToWindowJwtToken(onSet);

        cleanup = () => {
            clearTimeout(timer);
            unsubscribe();
        };
    });
}

export function readRefreshIntervalMsFromCookie(): number | undefined {
    const refreshInSec = parsePositiveSeconds(getCookie(SESSION_REFRESH_IN_SEC_COOKIE));

    if (refreshInSec !== undefined) {
        return getSecondsInMS(refreshInSec);
    }

    return undefined;
}

export function resolveSessionRefreshIntervalMs(): number {
    return readRefreshIntervalMsFromCookie() ?? DEFAULT_SESSION_REFRESH_INTERVAL_MS;
}

export function resolvePlatformRestartDelayMs(): number {
    const intervalMs = resolveSessionRefreshIntervalMs();
    const rawDelayMs = intervalMs - PLATFORM_RESTART_SAFETY_MARGIN_MS;
    const delayMs =
        rawDelayMs < MIN_PLATFORM_RESTART_DELAY_MS ? DEFAULT_PLATFORM_RESTART_DELAY_MS : rawDelayMs;

    return clampPlatformRestartDelayMs(delayMs);
}
